import {
  FluidAnchorPosition,
  FluidPopoverConfig,
  FluidPopoverTranslationOptions,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { createPopper, Instance, Placement, Rect } from '@popperjs/core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { CloseButton } from '../../common/fluid-common-button.components';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ESCAPE, EventUtils } from '../../utils/events/event-utils';
import {
  hasPopperAncestor,
  togglePopperListeners,
} from '../../utils/popperjs/popperjs.utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Popover
 *
 * @contextData FLUID Popover is a floating, non-actionable dialog meant to display a small amount of supplemental or contextual information without interrupting a user's workflow. Popover is always attached to a "trigger" element (such as an Icon, Badge, or Button) that opens the dialog on hover, focus, or click. Content within a Popover should be brief, helpful, and related to other content within the same page.
 *
 * @slot trigger - Named slot for Popover's "trigger" element (i.e. opens Popover on hover/:focus/click)
 * @slot content - Named slot for content displayed within Popover's container
 */
@Component({
  tag: 'fluid-popover',
  styleUrl: 'fluid-popover.less',
  shadow: true,
})
export class FluidPopover implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * True if browser is IE11, false if any other browser.
   */
  private _browserIsMsie: boolean = Utils.browserIsMsie();

  /**
   * Reference to PopperJS instance.
   */
  private _popperInstance: Instance;

  /**
   * True if Popover has PopperJS applied to an ancestor element,
   * false if not. When true, Popper's "strategy" needs to be set to
   * "absolute" so dropdown menu displays correctly (for some reason,
   * having a "fixed" Popper element as a child of another Popper element
   * with "fixed" strategy skews Popper's "translate" styles & causes the
   * element to appear offscreen).
   */
  private _hasPopperAncestor: boolean;

  /**
   * Reference to the "trigger" slot's wrapper button element.
   */
  private _triggerRef: HTMLElement;

  /**
   * Reference to Popover container (i.e. "popup/tooltip" root).
   */
  private _containerRef: HTMLElement;

  /**
   * Monitors whether the Popover trigger element is currently visible
   * within the viewport. If trigger is NOT visible, hides the Popover
   * container & disables PopperJS event listeners.
   */
  private _visibilityObserver: IntersectionObserver;

  /**
   * setTimeout delay before Popover opens.
   */
  private _delayOpen;

  /**
   * setTimeout delay before Popover closes.
   */
  private _delayClose;

  /**
   * `id` attribute applied to Popover container (for accessibility).
   */
  private _containerId: string = generateUniqueId();

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidPopoverElement;

  // ================================================================ //
  // -- States

  /**
   * Whether the Popover is open or closed.
   */
  @State() _open = false;

  /**
   * Whether the Popover should persist after mouseLeave. Set to true
   * when trigger or Popover container clicked (IFF "persistable" Prop
   * is true AND both "asWrapper" & "disableInteraction" Props are
   * false). Also controls displaying "close" button.
   */
  @State() _persistent = false;

  /**
   * Cues Popover to be hidden from view without actually closing.
   * When true, Popover will begin to fade out (without calling
   * "closePopover()"). When fading animation ends, "_inert" is set
   * to true & "display: none;" is applied to the Popover container.
   */
  @State() _inertCue = true;

  /**
   * Used to hide Popover from view by setting "display: none;" on
   * Popover container.
   */
  @State() _inert = true;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Text for Popover header title. If provided, Popover will render a
   * "header" section at the top of the Popover container. If omitted,
   * Popover will NOT render a header section (similar to a tooltip).
   */
  @Prop({ mutable: true }) header: string;

  /**
   * Preferred side of trigger element to anchor Popover container.
   * When open, Popover will appear on the specified side UNLESS it's
   * projected to overflow a clipping boundary (i.e. it would appear
   * offscreen or would be truncated by a parent container).
   */
  @Prop({ mutable: true }) placement: FluidAnchorPosition =
    FluidAnchorPosition.TOP;

  /**
   * Sets the Popover container's max-width (in px). If the total
   * width of Popover's content is LESS than the value of this Prop,
   * the Popover container will automatically shrink to fit content width.
   */
  @Prop({ mutable: true }) width = 320;

  /**
   * Sets delay (in ms) before Popover opens on hover. Prevents
   * accidental opening on hover. Only applies to hover events.
   */
  @Prop({ mutable: true }) hoverOpenDelay = 100;

  /**
   * Sets delay (in ms) before Popover closes when mouse leaves trigger.
   * Prevents accidental close if hover state is interrupted. Only applies
   * to hover events.
   */
  @Prop({ mutable: true }) hoverCloseDelay = 400;

  /**
   * Indicates that Popover's "trigger" slot contains a clickable
   * / focusable element (e.g. FLUID Button). When true, Popover
   * will NOT be persistable, will ONLY open on hover events
   * (i.e. trigger clicks will NOT open the Popover), & will NOT
   * allow :focus (however, the SLOTTED element CAN receive :focus).
   *
   */
  @Prop({ mutable: true }) asWrapper = false;

  /**
   * Whether the Popover is visible (true) or hidden (false).
   */
  @Prop({ mutable: true }) open = false;

  @Watch('open')
  externalOpenHandler() {
    this.open ? this.openPopover() : this.closePopover();
  }

  /**
   * If true, the popover will not allow any interaction on its
   * trigger other than hover (i.e, it will NOT be persistable).
   */
  @Prop({ mutable: true }) disableInteraction = false;

  /**
   * Content displayed within the Popover container (supports basic
   * HTML). NOTE: if this Prop is provided, the "content" Slot will
   * NOT be available.
   */
  @Prop({ mutable: true }) message: string;

  /**
   * When true, Popover will NOT open on hover or click events
   * (e.g. only open popover when a button is in a disabled state).
   */
  @Prop({ mutable: true }) preventPopoverOpening = false;

  /**
   * Allows Popover to remain open after hover event ends or trigger
   * loses :focus (instead of closing after given delay). Clicking the
   * trigger element or Popover container will cause it to persist.
   */
  @Prop({ mutable: true }) persistable = true;

  /**
   * When TRUE, Popover has "position: fixed;" & will only adjust its
   * placement and/or position to prevent its container from appearing
   * offscreen. When FALSE, Popover has "position: absolute" & will
   * adjust its placement and/or positioning to stay within the bounds
   * of any parent container(s) that would truncate overflow (e.g.
   * scrollable elements, elements with "overflow: hidden;" etc.). This
   * Prop should be TRUE for most implementations - however, setting it
   * to false may help to improve performance.
   */
  @Prop({ mutable: true }) ignoreClippingBoundaries = true;

  @Watch('placement')
  @Watch('ignoreClippingBoundaries')
  onPopperOptionsChange() {
    this.updatePopperOptions();
  }

  /**
   * Allows Popover @Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidPopoverConfig;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when Popover is opened or closed. Emits "true" when
   * Popover is opened & "false" when closed.
   */
  @Event() popoverToggled: EventEmitter<boolean>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: selectiveMapConfigToLocal - set local @Props to "config" values
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - set component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config); // Run before setGlobalTheme() to allow config to set theme
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    useI18n.bind(this)();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad(): Promise<void> | void {
    if (this.open) this.openPopover(true);
  }

  /**
   * Called once just after the component fully loaded.
   * @action: createPopperInstance - initialize PopperJS instance
   * @action: initializeVisibilityObserver - initialize IntersectionObserver
   */
  componentDidLoad() {
    this.createPopperInstance();
    this.initializeVisibilityObserver();
  }

  /**
   * Called when component will be updated due to @Prop or @State change.
   * @action: setPopperStrategy - sets Popper strategy to "absolute" if an ancestor Popper element exists
   * @action: update - update PopperJS instance before upcoming render
   */
  componentWillUpdate() {
    this.setPopperStrategy();

    if (!this._popperInstance) {
      this.createPopperInstance();
    } else {
      this._popperInstance?.update().then();
    }
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action: removeVisibilityObserver - destroy IntersectionObserver
   * @action: destroy - removes PopperJS instance
   */
  disconnectedCallback() {
    this.removeVisibilityObserver();
    this._popperInstance?.destroy();
    this._popperInstance = undefined;
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Event listener for "focusin" events on host. Opens Popover when
   * :focus is transferred INTO Popover's bounds (includes :focus event
   * on slotted "trigger" element).
   */
  @Listen('focusin')
  onFocusIn() {
    this.clearDelayTimeouts();
    if (this._open) return;
    this.openPopover();
  }

  /**
   * Event listener for "focusout" events on host. Closes Popover when
   * :focus is transferred OUT of Popover's bounds (includes blur event
   * on slotted "trigger" element).
   */
  @Listen('focusout')
  onFocusOut() {
    if ((this._persistent && this.persistable) || !this._open) return;
    this.closePopover();
  }

  /**
   * Event listener for "keydown" events on host. Closes Popover when
   * "escape" key is pressed.
   */
  @Listen('keydown', { capture: true })
  onKeyDown(event: KeyboardEvent) {
    if (!EventUtils.isKey(event, ESCAPE)) return;
    event.stopPropagation();
    this.closePopover();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Clears all active hover open / close delays & prevents setTimout
   * delays from overlapping.
   */
  clearDelayTimeouts(): void {
    clearTimeout(this._delayOpen);
    clearTimeout(this._delayClose);
  }

  /**
   * Returns true if Popover has the ability to be persistent.
   */
  canPersist(): boolean {
    return this.persistable && !this.asWrapper && !this.disableInteraction;
  }

  /**
   * Checks all ancestor elements for PopperJS - if Popover is
   * a child of another Popper element, sets Popper strategy to
   * "absolute" to ensure dropdown menu displays correctly (for some
   * reason, having a "fixed" Popper element as a child of another Popper
   * element with "fixed" strategy skews Popper's "translate" styles &
   * causes the element to appear offscreen).
   */
  setPopperStrategy(): void {
    if (!isNullOrUndefined(this._hasPopperAncestor) || !this._open) return;
    this._hasPopperAncestor = hasPopperAncestor(this.host);
    if (this._hasPopperAncestor) {
      this.ignoreClippingBoundaries = false;
    }
  }

  /**
   * If "ignoreClippingBoundaries" Prop AND "parentContainer" Prop are
   * provided, sets PopperJS "boundary" to the parent container element -
   * otherwise, keeps PopperJS default boundary.
   */
  getPopperBoundary(): HTMLElement | string {
    if (!!this.parentContainer && !this.ignoreClippingBoundaries) {
      return this.parentContainer;
    } else {
      return 'clippingParents';
    }
  }

  /**
   * Creates PopperJS instance & sets options based on Prop values.
   */
  createPopperInstance(): void {
    if (!!this._popperInstance || !this._containerRef || !this._triggerRef)
      return;

    this._popperInstance = createPopper(this._triggerRef, this._containerRef, {
      strategy: this.ignoreClippingBoundaries ? 'fixed' : 'absolute',
      placement: this.placement,
      modifiers: [
        {
          name: 'eventListeners',
          enabled: this._open,
        },
        {
          name: 'offset',
          options: { offset: [0, 10] },
        },
        {
          name: 'arrow',
          options: { padding: 8 },
        },
        {
          name: 'flip',
          options: { boundary: this.getPopperBoundary() },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
            boundary: this.getPopperBoundary(),
            tetherOffset: ({ reference, placement }) =>
              this.getTetherOffset(reference, placement),
          },
        },
      ],
    });
  }

  /**
   * Updates PopperJS instance When "placement", "ignoreClippingBoundaries",
   * or "parentContainer" Props are updated.
   */
  updatePopperOptions(): void {
    this._popperInstance?.setOptions((options) => ({
      ...options,
      strategy: this.ignoreClippingBoundaries ? 'fixed' : 'absolute',
      placement: this.placement,
      modifiers: [
        ...options.modifiers,
        {
          name: 'flip',
          options: { boundary: this.getPopperBoundary() },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
            boundary: this.getPopperBoundary(),
            tetherOffset: ({ reference, placement }) =>
              this.getTetherOffset(reference, placement),
          },
        },
      ],
    }));
  }

  /**
   * Calculates trigger element's X or Y axis center for positioning
   * Popover's arrow. Used in PopperJS "preventOverflow" options.
   * @param reference - the trigger element's current Rect
   * @param placement - the current dynamic placement of the container element
   */
  getTetherOffset(reference: Rect, placement: Placement): number {
    switch (true) {
      case placement.includes(FluidAnchorPosition.TOP):
      case placement.includes(FluidAnchorPosition.BOTTOM):
        return reference.width / 2;
      case placement.includes(FluidAnchorPosition.LEFT):
      case placement.includes(FluidAnchorPosition.RIGHT):
        return reference.height / 2;
      default:
        return 0;
    }
  }

  /**
   * Initializes IntersectionObserver that monitors whether Popover is
   * visible within the viewport. If Popover is OPEN but NOT currently
   * visible, hides Popover from view (without closing).
   */
  initializeVisibilityObserver(): void {
    if (this._browserIsMsie) return;

    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!this._open) return;
          this.setInert(!entry.isIntersecting);
        });
      },
      { root: null }
    );
    this._visibilityObserver.observe(this._triggerRef);
  }

  /**
   * Removes IntersectionObserver when Popover is disconnected from DOM.
   */
  removeVisibilityObserver(): void {
    if (!this._visibilityObserver) return;
    this._visibilityObserver.unobserve(this._triggerRef);
    this._visibilityObserver = undefined;
  }

  /**
   * Opens Popover if closed OR sets Popover to be persistent if already
   * open, then emits "popoverToggled" event containing Popover's "open" status.
   * @param persistent - whether Popover should remain open
   */
  openPopover(persistent = false) {
    if (this.preventPopoverOpening) return;

    this.clearDelayTimeouts();
    this._persistent = this.canPersist() && persistent;
    if (this._open) return;

    this.setInert(false);
    this._open = true;
    this.popoverToggled.emit(this._open);
  }

  /**
   * Closes Popover & removes persistent status, then cues "inert" status
   * (i.e. "display: none;") & emits "popoverToggled" event.
   */
  closePopover(): void {
    this.clearDelayTimeouts();
    this.open = false;
    this._open = false;
    this._persistent = false;
    this.setInert(true);
    this.popoverToggled.emit(this._open);
  }

  /**
   * Opens Popover after delay specified in "hoverOpenDelay" Prop if
   * Popover isn't currently persistent. Called on mouseEnter events
   * on trigger element or Popover container.
   */
  hoverOpen(): void {
    if (this._persistent) return;
    this.clearDelayTimeouts();

    if (!this._open) {
      this._delayOpen = setTimeout(() => {
        this.openPopover();
      }, this.hoverOpenDelay);
    }
  }

  /**
   * Closes Popover after delay specified in "hoverCloseDelay" Prop
   * if Popover isn't already closed or set to persistent. Called
   * on mouseLeave events on trigger element or Popover container.
   */
  hoverClose(): void {
    this.clearDelayTimeouts();
    if (this._persistent || !this._open) return;

    this._delayClose = setTimeout(() => {
      this.closePopover();
    }, this.hoverCloseDelay);
  }

  /**
   * Toggles Popover open / closed when Trigger is clicked IFF "asWrapper"
   * AND "disableInteraction" are false. Called on trigger click events.
   * If Popover closed - opens Popover immediately & sets to persistent.
   * If Popover is open but NOT persistent - sets Popover to persistent.
   * If Popover is open AND persistent - closes Popover.
   */
  handleTriggerClick(): void {
    this.clearDelayTimeouts();
    if (this.disableInteraction || this.asWrapper) return;

    if (this._open && this._persistent) {
      this.closePopover();
    } else {
      this.openPopover(true);
    }
  }

  /**
   * Sets Popover to persistent when Popover container is clicked.
   * @param event - click event to prevent from bubbling
   */
  handleContainerClick(event: MouseEvent): void {
    this.clearDelayTimeouts();
    this.asWrapper && event.stopPropagation(); // Prevent event from bubbling (e.g. trigger button click)
    if (this.disableInteraction) return;
    this.openPopover(true);
  }

  /**
   * Closes the Popover when "x" (close) button is clicked.
   * @param event - click event to prevent from bubbling to Popover container
   */
  handleDismissClick(event: MouseEvent): void {
    if (!this._open) return;
    event.stopImmediatePropagation();
    this.closePopover();
  }

  /**
   * Sets or resets Popover "inert" state & enables/disables PopperJS
   * event listeners. "Inert" state allows Popover to be hidden from view
   * while still open when trigger element is not visible within viewport.
   * "Inert" state also used to set "display: none;" on Popover container
   * when closed. When "_inertCue" is set to true, "_inert" variable will
   * be set to "true" when "fade out" animation ends - "_inert" variable
   * controls "display: none;" class applied to Popover container.
   * @param inert - whether to enable or disable "inert" state
   */
  setInert(inert: boolean): void {
    this._inertCue = inert;
    if (!inert) this._inert = false;
    togglePopperListeners(this._popperInstance, !inert);
  }

  // ================================================================ //
  // -- Render

  render() {
    return (
      <span class={`fluid-popover ${this.theme}`}>
        <button
          ref={(el) => (this._triggerRef = el as HTMLElement)}
          tabIndex={this.asWrapper ? -1 : 0}
          onMouseEnter={() => this.hoverOpen()}
          onMouseLeave={() => this.hoverClose()}
          onClick={() => this.handleTriggerClick()}
          aria-expanded={String(!!this._open)}
          aria-controls={this._containerId}
          aria-label={this.header ?? 'Tooltip'}
          class={{
            'popover-trigger': true,
            'as-wrapper': this.asWrapper,
          }}>
          <slot name='trigger' />
        </button>
        <div
          role='tooltip'
          id={this._containerId}
          ref={(el) => (this._containerRef = el as HTMLElement)}
          onMouseEnter={() => this.hoverOpen()}
          onMouseLeave={() => this.hoverClose()}
          onClick={(event) => this.handleContainerClick(event)}
          onAnimationEnd={() => (this._inert = this._inertCue)}
          class={{
            'popover-container': true,
            'as-wrapper': this.asWrapper,
            persistable: this.persistable && !this.disableInteraction,
            persistent: this._persistent && this.persistable,
            'popover-visible': !this._inertCue && this._open,
            'popover-hidden': this._inertCue || !this._open,
            'popover-inert': this._inert,
          }}
          style={{
            'max-width': `${this.width}px`,
            'z-index': `${this.zIndex}`,
            ...(this._browserIsMsie && {
              width: `${this.width}px`,
            }),
          }}>
          <div class='popover-body'>
            {(!!this.header || !!this.fluidTranslate?.header) && (
              <div
                class='popover-header'
                innerHTML={
                  this._i18nStore?.translate(this.fluidTranslate?.header) ||
                  this.header
                }></div>
            )}
            <div class='popover-content'>
              {!!this.message || !!this.fluidTranslate?.message ? (
                <div
                  innerHTML={
                    this._i18nStore?.translate(this.fluidTranslate?.message) ||
                    this.message
                  }
                />
              ) : (
                <slot name='content' />
              )}
            </div>
            <span
              class='popover-arrow'
              role='presentation'
              data-popper-arrow={true}
            />
            <CloseButton
              class='popover-dismiss'
              render={!!this._persistent}
              title={
                this._i18nStore?.translate(
                  this.fluidTranslate?.dismissButton?.label
                ) || 'Close Popover'
              }
              small={true}
              theme={this.theme}
              onClick={(event: MouseEvent) => this.handleDismissClick(event)}
            />
          </div>
        </div>
      </span>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * If specified, Popover's edge detection will apply to the given element
   * (rather than to the window).
   * @internal
   */
  @Prop({ mutable: true }) parentContainer: HTMLElement;

  @Watch('parentContainer')
  onParentContainerChange() {
    this.updatePopperOptions();
  }

  /**
   * Overrides default CSS z-index value for Popover container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidPopoverTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
}
